import { getValidAuthHeaders } from '../lib/auth';
import type { ShopifyOrderLineItem } from '../types/shopify';

export interface OrderLiveStatusResult {
  changed: boolean;
  cancelled: boolean;
  financialStatus: string | null;
  fulfillmentStatus: string | null;
  cancelledAt: string | null;
  lineItems?: ShopifyOrderLineItem[];
  lineItemsUpdated?: boolean;
  error?: string;
}

export async function checkShopifyOrderLiveStatus(
  shopifyOrderId: number | string
): Promise<OrderLiveStatusResult> {
  const headers = await getValidAuthHeaders();
  if (!headers) {
    return {
      changed: false,
      cancelled: false,
      financialStatus: null,
      fulfillmentStatus: null,
      cancelledAt: null,
      error: 'Not authenticated',
    };
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-order-live-status`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ shopifyOrderId: Number(shopifyOrderId) }),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message =
        (typeof data?.error === 'string' && data.error) ||
        (typeof data?.message === 'string' && data.message) ||
        `Failed to verify order status (${response.status})`;
      return {
        changed: false,
        cancelled: false,
        financialStatus: null,
        fulfillmentStatus: null,
        cancelledAt: null,
        error: message,
      };
    }

    return {
      changed: Boolean(data.changed),
      cancelled: Boolean(data.cancelled),
      financialStatus: data.financialStatus ?? null,
      fulfillmentStatus: data.fulfillmentStatus ?? null,
      cancelledAt: data.cancelledAt ?? null,
      lineItems: Array.isArray(data.lineItems) ? data.lineItems : undefined,
      lineItemsUpdated: Boolean(data.lineItemsUpdated),
    };
  } catch (err) {
    return {
      changed: false,
      cancelled: false,
      financialStatus: null,
      fulfillmentStatus: null,
      cancelledAt: null,
      error: err instanceof Error ? err.message : 'Failed to verify order status',
    };
  }
}
